import { startTransition, useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { api, FOLLOWUP_BOARD, getToken, pressedMenu, setPageOnScreen, setUnauthorizedHandler, TOKEN_KEY, watchRead } from "./api/client";
import type { FollowupRound, Me } from "./api/types";
import { can } from "./util";
import { useInterval, useOnReturn } from "./hooks";
import { useFreshBuild, useStaleBuild } from "./fresh";
import { todoCounts } from "./customers";
import { forgetOrders, useOrders } from "./ordersStore";
import { afterBack, openLayer } from "./backstack";
import { forgetGrowth } from "./growthData";
import { themePrefNow, watchSystemTheme } from "./theme";
import { trackViewport } from "./viewport";
import { installTooltips } from "./tooltip";
import PhoneNav, { type NavItem } from "./components/PhoneNav";
import SideNav, { type SideItem, useSideFold, useSideSlide } from "./components/SideNav";
import NewsDrawer from "./components/NewsDrawer";
import type { ShellIconName } from "./components/ShellIcons";
import LoadBar from "./components/LoadBar";
import PageSkel, { usePageReading } from "./components/PageSkel";
import LeadsView from "./components/LeadsView";
import LoginGate from "./components/LoginGate";
import AccountMenu from "./components/AccountMenu";
import { healthShort, healthTone, useAIHealth } from "./components/AIHealth";
import PricesView from "./components/PriceBoard";
import { lazyView, prefetch, ViewShell } from "./views";
import { bootWhenQuiet } from "./bootScreen";
import { PageShown } from "./pageShown";

// Every page but 对话 and 价目表 is a download of its own (views.tsx), fetched when
// opened or in the quiet moments after sign-in: 对话 is what CS open first, and
// 价目表's drawer is part of a chat, so both come with the console. 今天 is where
// the console opens, so its file is asked for as this one starts (below), while
// sign-in is checked.
const loadToday = () => import("./components/TodayView");
const loadCustomers = () => import("./components/CustomersView");
const loadGrowth = () => import("./components/GrowthView");
const loadPlaybook = () => import("./components/PlaybookView");
const loadAI = () => import("./components/AIView");
const loadLibrary = () => import("./components/LibraryView");
const loadSettings = () => import("./components/SettingsView");
const loadUsers = () => import("./components/UsersView");
const loadLive = () => import("./components/LiveBoard");
const loadBoard = () => import("./components/ScriptBoardView");
const loadFollowup = () => import("./components/FollowupView");
const TodayView = lazyView(loadToday);
const CustomersView = lazyView(loadCustomers);
const GrowthView = lazyView(loadGrowth);
const PlaybookView = lazyView(loadPlaybook);
const AIView = lazyView(loadAI);
const LibraryView = lazyView(loadLibrary);
const SettingsView = lazyView(loadSettings);
const UsersView = lazyView(loadUsers);
const LiveBoard = lazyView(loadLive);
const ScriptBoardView = lazyView(loadBoard);
const FollowupView = lazyView(loadFollowup);

// live is 报表's 展示: the meeting room's screen, over everything, at #live so
// the TV can be pointed straight at it.
type View = "today" | "leads" | "customers" | "followup" | "prices" | "reports" | "board" | "playbook" | "ai" | "settings" | "users" | "live";
const VIEWS: View[] = ["today", "leads", "customers", "followup", "prices", "reports", "board", "playbook", "ai", "settings", "users", "live"];
type Page = Exclude<View, "live">;

/** Each page's name in the menu and its icon (ShellIcons; 小爱 is her orb). */
const PAGE: Record<Page, { label: string; icon: ShellIconName | "ai" }> = {
  today: { label: "今天", icon: "today" },
  leads: { label: "对话", icon: "leads" },
  // Two characters like every other page's name (owner 10/25: 「就叫客户…3个字看起来很突兀」).
  customers: { label: "客户", icon: "customers" },
  followup: { label: "跟进", icon: "followup" },
  prices: { label: "价目表", icon: "prices" },
  reports: { label: "经营", icon: "reports" },
  ai: { label: "小爱", icon: "ai" },
  board: { label: "话术榜", icon: "board" },
  playbook: { label: "剧本", icon: "playbook" },
  users: { label: "团队", icon: "users" },
  settings: { label: "设置", icon: "settings" },
};
/** The sidebar's four blocks (prototype v2.11): the day's work, the business, 小爱, the team. */
const SIDE_GROUPS: Page[][] = [
  ["today", "leads", "customers", "followup"],
  ["prices", "reports"],
  ["ai", "board", "playbook"],
  ["users", "settings"],
];
/**
 * The phone's bar: the first four of these this person may open, the rest
 * under 更多. The prototype's bar is 今天 · 对话 · 客户 · 经营; a colleague
 * without 经营 gets 跟进, the one they reach for next.
 */
const PHONE_ORDER: Page[] = ["today", "leads", "customers", "reports", "followup", "prices", "ai", "board", "playbook", "users", "settings"];

/** The address without its #: 「customers」, 「live/month」. */
const hashNow = () => window.location.hash.replace(/^#\/?/, "");

/**
 * The page named in the address — #customers — so a reload stays on it; none is 今天. The TV board's screen
 * comes after it, #live/month (LiveBoard reads that part): it used to be no page at all, and the address
 * was put back to 今天.
 */
function viewFromHash(): View {
  const h = hashNow();
  if (h === "live" || h.startsWith("live/")) return "live";
  // 素材库 is a tab of 小爱 now (owner 10/11); an old #library link opens it there.
  if (h === "library") {
    try {
      window.localStorage.setItem("ai.tab", "library");
    } catch {
      /* this visit only */
    }
    window.dispatchEvent(new CustomEvent("ai:tab", { detail: "library" }));
    return "ai";
  }
  return (VIEWS as string[]).includes(h) ? (h as View) : "today";
}
/**
 * A page's place in the window. A page left stays mounted, hidden, and says it is off screen (pageShown.ts): its
 * drawers, drawn on <body>, hide with it instead of staying over the next page.
 */
function Viewport({ on, warm, children }: { on: boolean; warm?: boolean; children: ReactNode }) {
  return (
    <div className={`viewport${on ? "" : " hidden"}${warm ? " warm" : ""}`}>
      <PageShown.Provider value={on}>{children}</PageShown.Provider>
    </div>
  );
}

// The console opens on 今天: its file is on its way while sign-in is checked.
if (viewFromHash() === "today") void TodayView.preload().catch(() => undefined);

/** 跟进's 可以直接发, every label added up: the customers the console can still send the round to (FollowupView). */
const sendableOf = (r?: FollowupRound | null) =>
  (r?.groups ?? []).reduce((n, g) => n + g.people.filter((p) => p.window_until && !p.done).length, 0);
/** How often the console reads 跟进's round for its number while that page is not open (it reads its own every minute). */
const SENDABLE_MS = 5 * 60_000;

export default function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [booting, setBooting] = useState(true);
  const [notice, setNotice] = useState("");
  // Held in the address as well as here: a reload used to drop everybody back on
  // 对话 whatever they had open.
  const [view, setView] = useState<View>(viewFromHash);
  // The views below are memo'd: a menu press or a number arriving re-renders
  // this frame, and it used to redraw every view opened so far with it — the
  // whole customer table and the open chat — before the press painted. What
  // they are handed stays the same object for the same reason.
  // Customers waiting for an answer (对话's 回复客户): the red number beside 对话,
  // the way WhatsApp puts its unread count on Chats.
  const [waiting, setWaiting] = useState(0);
  // Every work list's number (LeadsView, every minute) and the shop's orders
  // (ordersStore, read from sign-in): 今天要做, the grey number beside 今天.
  const [counts, setCounts] = useState<Record<string, number> | null>(null);
  // LeadsView hands over a new object every minute: the same numbers keep the one already here, or the console and
  // every page kept but not on screen redrew each minute for nothing (production's onCount did the same check).
  const onCounts = useCallback((c: Record<string, number>) => {
    setCounts((prev) => {
      if (!prev) return c;
      const keys = Object.keys(c);
      return keys.length === Object.keys(prev).length && keys.every((k) => prev[k] === c[k]) ? prev : c;
    });
  }, []);
  const shop = useOrders(can(me, "view_leads"));
  // 跟进's grey number (prototype v2.20): who can be sent the round now. Heard from every read of the round —
  // the page's own, each minute while it is open — and read quietly once the console is up and every few
  // minutes after (below), so it is there before 跟进 is opened.
  const [sendable, setSendable] = useState(0);
  useEffect(() => watchRead<{ round: FollowupRound }>(FOLLOWUP_BOARD, (r) => setSendable(sendableOf(r?.round))), []);
  // 对话's list has come once (its count; the menu shows only who waits): its
  // layout is then made ahead, off screen (warmLeads below).
  const [leadsListed, setLeadsListed] = useState(false);
  // And again once the chat it opens by itself is drawn (LeadsView onChatDrawn): a pass per step, -1 for none yet.
  const [leadsDrawn, setLeadsDrawn] = useState(0);
  const warmed = useRef(-1);
  const chatDrawn = useCallback(() => setLeadsDrawn((n) => n + 1), []);
  // A chat is open in 对话 (LeadsView's .wc.open): with 对话 on screen, a phone hides its bar (styles.css .chat-open).
  const [chatOpen, setChatOpen] = useState(false);
  // 小爱's connection, for the dot on her tab and the line on her page.
  const health = useAIHealth(can(me, "edit_ai"));
  const aiDot = healthTone(health);
  // The page a phone's chat was opened from, so closing it goes back there.
  const backTo = useRef<View | null>(null);
  useEffect(() => trackViewport(), []);
  // Every title as the console's own tooltip, not the browser's grey box (owner 10/11).
  useEffect(() => installTooltips(), []);
  // A conversation the customer table asked to open; seq makes the same one
  // openable twice in a row.
  const [openReq, setOpenReq] = useState<{ wa: string; seq: number; from?: "followup" } | null>(null);
  // Views are mounted on first visit and then kept, so their state survives tab
  // switches without paying for data nobody has asked to see.
  const [visited, setVisited] = useState<View[]>(["leads"]);
  // Resuming with an expired token is an ordinary cold start, not a session
  // that "ended" under the operator — no scary banner for it.
  const bootingRef = useRef(true);

  const reset = useCallback(() => {
    setMe(null);
    setWaiting(0); // never show one user's numbers to the next one
    setCounts(null);
    setSendable(0);
    forgetOrders();
    setLeadsListed(false);
    setLeadsDrawn(0);
    warmed.current = -1;
    forgetGrowth(); // nor their 增长 report: it carries costs and names by permission
  }, []);
  // The look follows the device while it is on 系统 — a phone going dark at sunset.
  useEffect(() => watchSystemTheme(themePrefNow), []);

  // A 401 anywhere drops straight back to the sign-in screen; the client has
  // already discarded the token by the time this runs.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      reset();
      if (!bootingRef.current) setNotice("登录已过期，请重新登录");
    });
  }, [reset]);

  // Resume an existing session on load.
  useEffect(() => {
    if (!getToken()) {
      bootingRef.current = false;
      setBooting(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const m = await api.me();
        if (!cancelled) setMe(m);
      } catch {
        /* invalid/expired token: fall through to the sign-in screen */
      } finally {
        if (!cancelled) {
          bootingRef.current = false;
          setBooting(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // The loading screen (index.html, or the one signing in puts up) goes as soon as the sign-in check has answered,
  // as production's console showed itself then: once the type has come (index.html loads it without holding the
  // first paint; at most .6 s more, so a slow font never holds the console) and the console under it has drawn a few
  // frames in a row on time (at most .4 s). It used to stay at least 1.3 s into the page and up to 3.5 s. Signed
  // out, the sign-in page lets it go when it is ready (LoginGate).
  useEffect(() => {
    if (booting || !me) return;
    let gone = false;
    const off = () => {
      if (gone) return;
      gone = true;
      bootWhenQuiet(0, 400);
    };
    const t = window.setTimeout(off, 600);
    void document.fonts?.ready.then(off, off);
    return () => window.clearTimeout(t);
  }, [booting, me]);

  // Another tab signing in or out swaps the shared token underneath us; keeping
  // this tab's identity would attribute its actions to the wrong account.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== TOKEN_KEY) return;
      if (!e.newValue) {
        reset();
        setNotice("你在另一个分页登出了");
        return;
      }
      void api
        .me()
        .then(setMe)
        .catch(() => reset());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [reset]);

  // A customer in 今天's 要你做: the chat, and back to 今天 when it closes.
  const openFromToday = useCallback((wa: string) => {
    setOpenReq({ wa, seq: Date.now() });
    setView("leads");
    backTo.current = "today";
  }, []);
  // A conversation 小爱 carried alone, from 接待数据: back to 小爱 when it closes.
  const openFromAI = useCallback((wa: string) => {
    setOpenReq({ wa, seq: Date.now() });
    setView("leads");
    backTo.current = "ai";
  }, []);
  const openFromCustomers = useCallback((wa: string) => {
    setOpenReq({ wa, seq: Date.now() });
    setView("leads");
    backTo.current = "customers";
  }, []);
  // A name on 跟进: the chat, and back to 跟进 when it closes.
  const openFromFollowup = useCallback((wa: string) => {
    setOpenReq({ wa, seq: Date.now(), from: "followup" });
    setView("leads");
    backTo.current = "followup";
  }, []);
  // A name in 报表's 老客 list: the chat, and back to 报表 when it closes.
  const openFromReports = useCallback((wa: string) => {
    setOpenReq({ wa, seq: Date.now() });
    setView("leads");
    backTo.current = "reports";
  }, []);
  // A phone's chat closed (its arrow, or the phone's back): back to 客户表 if
  // that is where it was opened, as a chat opened from a list goes back to it.
  const chatClosed = useCallback(() => {
    const to = backTo.current;
    backTo.current = null;
    if (to) setView(to);
  }, []);
  // A menu item: its page, unless that is the page on screen. Pressed again
  // while its page is read, it does nothing — no second read, no redraw.
  //
  // The menu takes the press at once, in a render of its own (picked: the item lights and the pill slides), and the
  // page follows as a transition, drawn in slices the browser can paint between. Both in one render, the press waited
  // for the page leaving and the page coming to be drawn whole — 0.13–0.25 s of script before anything moved, 0.44 s
  // to the next paint going to 对话 (owner's path, 10/27) — and a second press in that time queued behind it. A press
  // on another item meanwhile replaces the page on its way. The page starts once the press has been painted: started
  // with it, a page quick to draw was drawn before that paint, and a phone's 客户 put its 0.18 s of layout between the
  // press and the bar lighting up.
  const shown = useRef<View>("today");
  const [picked, setPicked] = useState<View | null>(null);
  const pickedNow = useRef<View | null>(null);
  const go = useCallback((v: View) => {
    if (v === (pickedNow.current ?? shown.current)) return;
    pressedMenu(v);
    pickedNow.current = v;
    setPicked(v);
    requestAnimationFrame(() => window.setTimeout(() => startTransition(() => setView(v)), 0));
  }, []);
  const pick = useCallback(
    (v: string) => {
      if (v === (pickedNow.current ?? shown.current)) return;
      backTo.current = null;
      go(v as View);
    },
    [go],
  );
  const openAI = useCallback(() => setView("ai"), []);
  // 退出 the board: back to the page it was opened from, 今天 where it was opened at #live itself. It always went to 经营 (owner
  // 10/27: 「为什么我点会议室大屏全屏但是选退出后会切到经营板块里？」).
  const beforeLive = useRef<View | null>(null);
  const exitLive = useCallback(() => setView(beforeLive.current ?? "today"), []);
  // 增长's 没回 / 回访 chips: the customer table, where those people are listed.
  const openCustomers = useCallback(() => {
    backTo.current = null;
    setView("customers");
  }, []);
  const reloadMe = useCallback(() => {
    void api
      .me()
      .then(setMe)
      .catch(() => undefined);
  }, []);
  // Where somebody lands, and where a page they may not open sends them: 今天
  // for whoever works the lists or reads the report (the prototype's home),
  // else the first page their role opens. 对话 used to be everyone's, and a
  // role that may only read the report (「只看报表」, 2026-09-30) landed on it
  // and was refused there.
  const mayToday = !me || can(me, "view_leads") || can(me, "view_reports");
  const home: View = mayToday
    ? "today"
    : can(me, "edit_playbook")
      ? "playbook"
      : can(me, "edit_ai")
        ? "ai"
        : can(me, "edit_settings")
          ? "settings"
          : can(me, "manage_users")
            ? "users"
            : "today";
  // Roles without the permission never render the view in the first place, so
  // no request is fired that is destined to 403.
  const safeView: View =
    view === "today" && !mayToday
      ? home
      : (view === "leads" || view === "customers" || view === "followup" || view === "prices" || view === "board") && !can(me, "view_leads")
      ? home
      : (view === "reports" || view === "live") && !can(me, "view_reports")
      ? home
      : view === "users" && !can(me, "manage_users")
        ? home
        : view === "settings" && !can(me, "edit_settings")
          ? home
          : view === "playbook" && !can(me, "edit_playbook")
            ? home
            : view === "ai" && !can(me, "edit_ai")
              ? home
              : view;
  // A role that opens nothing at all: say so, rather than a refused 今天.
  const nothing = !!me && home === "today" && !mayToday;

  // The page on screen, for the reads it starts (client.ts) — before its
  // effects run and ask for anything.
  useLayoutEffect(() => {
    shown.current = safeView;
    setPageOnScreen(safeView);
    pickedNow.current = null;
    if (safeView !== "live") beforeLive.current = safeView;
  }, [safeView]);
  // The page picked has come (or another way of moving won): the menu follows the page again. After the paint, not
  // in the commit: until then picked is the page itself.
  useEffect(() => setPicked(null), [safeView]);
  // What the menu shows as open: the item pressed, from the press on, while its page is on its way.
  const menuView: View = picked && picked !== safeView ? picked : safeView;
  // Its menu item turns while the page is on its first read (its placeholder is up), and only then: a page already
  // drawn being read again is not something to wait for (client.ts).
  const firstRead = usePageReading(safeView);
  const pending = firstRead ? safeView : "";

  // The sidebar folded or open, and the page sliding with it (SideNav.tsx).
  const fold = useSideFold(safeView);
  useSideSlide(fold);
  // A newer build is waiting: the logo row says so, or 更多 on a phone.
  const fresh = useStaleBuild();
  // 更新了什么: a step of history, so the phone's back closes it.
  const [newsOpen, setNewsOpen] = useState(false);
  const closeNews = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (!newsOpen) return;
    const c = openLayer(() => {
      closeNews.current = null;
      setNewsOpen(false);
    });
    closeNews.current = () => {
      closeNews.current = null;
      c();
      setNewsOpen(false);
    };
    return () => closeNews.current?.();
  }, [newsOpen]);
  const openNews = useCallback(() => setNewsOpen(true), []);
  const shutNews = useCallback(() => closeNews.current?.(), []);

  useEffect(() => {
    setVisited((v) => (v.includes(safeView) ? v : [...v, safeView]));
  }, [safeView]);
  // The page picked is drawn in the render that picks it — its placeholder
  // with it — not one effect later: in between, the press showed a blank page
  // and nothing turning.
  const drawn = visited.includes(safeView) ? visited : [...visited, safeView];

  // 对话 laid out once, off screen, in the first quiet moment after its list
  // has come (styles.css .warm): the console used to open on it, and opened on
  // 今天 its first showing was one 117–193 ms frame on the owner's screen;
  // warmed, 50–83 ms (production build, real GPU, 125%, 10/23).
  const leadsCount = useCallback(() => setLeadsListed(true), []);
  const [warmLeads, setWarmLeads] = useState(false);
  useEffect(() => {
    if (!leadsListed || warmed.current === leadsDrawn) return;
    let live = true;
    let r1 = 0;
    let r2 = 0;
    const warm = () => {
      if (!live) return;
      warmed.current = leadsDrawn;
      setWarmLeads(true);
      r1 = requestAnimationFrame(() => {
        r2 = requestAnimationFrame(() => setWarmLeads(false));
      });
    };
    const w = window as Window & { requestIdleCallback?: (f: () => void, o?: { timeout: number }) => number };
    const t = window.setTimeout(() => (w.requestIdleCallback ? w.requestIdleCallback(warm, { timeout: 3000 }) : warm()), 500);
    return () => {
      live = false;
      window.clearTimeout(t);
      cancelAnimationFrame(r1);
      cancelAnimationFrame(r2);
      setWarmLeads(false);
    };
  }, [leadsListed, leadsDrawn]);

  // The pages this person may open are fetched in the quiet moments after
  // sign-in, one after another, so each tab opens at once; nobody downloads a
  // page their role cannot open. The TV board is fetched only for the TV.
  useEffect(() => {
    if (!me) return;
    const want: Array<() => Promise<unknown>> = [];
    if (can(me, "view_leads") || can(me, "view_reports")) want.push(TodayView.preload);
    if (can(me, "view_leads")) want.push(CustomersView.preload, FollowupView.preload, ScriptBoardView.preload);
    if (can(me, "view_reports")) want.push(GrowthView.preload);
    if (can(me, "edit_ai")) want.push(AIView.preload, LibraryView.preload);
    if (can(me, "edit_playbook")) want.push(PlaybookView.preload);
    if (can(me, "edit_settings")) want.push(SettingsView.preload);
    if (can(me, "manage_users")) want.push(UsersView.preload);
    return prefetch(want);
  }, [me]);

  // The address follows the page, and a changed address (back, a typed #) moves
  // the page. Not before sign-in: the page asked for is kept until it can be shown.
  // Written once a phone layer's own back has landed (backstack.ts), and with
  // the step's state kept: that state is how back knows a chat is open. An
  // address already on the page is left as it is: #live/month keeps its screen.
  useEffect(() => {
    if (!me) return;
    const want = safeView === "today" ? "" : safeView;
    afterBack(() => {
      const h = hashNow();
      if (h !== want && (!want || h.split("/")[0] !== want)) {
        window.history.replaceState(window.history.state, "", want ? `#${want}` : window.location.pathname + window.location.search);
      }
    });
  }, [me, safeView]);
  useEffect(() => {
    const onHash = () => setView(viewFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Tells the server this person is on the console, for the team page's 在线:
  // the pages that never poll would otherwise go quiet. Hidden tabs too — the
  // browser slows the timer to once a minute, which is all it needs.
  useInterval(() => void api.me().catch(() => undefined), 60000, !!me);

  // 跟进's number, read for the menu (sendable above): a few seconds after sign-in, past the first page's own
  // reads, then every few minutes while 跟进 itself is not open. Quiet: a number, never what a page waits for.
  const mayLeads = can(me, "view_leads");
  const readRound = useCallback(() => {
    if (!document.hidden) void api.followupBoard(true).catch(() => undefined);
  }, []);
  useEffect(() => {
    if (!mayLeads) return;
    const t = window.setTimeout(readRound, 4000);
    return () => window.clearTimeout(t);
  }, [mayLeads, readRound]);
  const roundPoll = mayLeads && safeView !== "followup";
  useInterval(readRound, SENDABLE_MS, roundPoll, { fireOnEnable: false });
  // A beat skipped in a background tab is asked for on coming back, at most once a minute.
  useOnReturn(readRound, roundPoll, 60_000);

  // A tab left open takes a new deploy by itself (fresh.tsx); the TV board as
  // soon as it can, every other page only where nobody is in the middle of something.
  useFreshBuild(safeView, safeView === "live");

  async function signOut() {
    // Drop the UI immediately: the pane shows customer PII and must not linger
    // while a slow logout request completes.
    reset();
    setNotice("");
    setView("today");
    await api.logout();
  }

  if (booting) return null; // avoids a sign-in flash while the session resumes
  if (!me) {
    return (
      <LoginGate
        notice={notice}
        onSignedIn={(m) => {
          setNotice("");
          setMe(m);
        }}
      />
    );
  }

  // The pages this person may open, each with what waits there: the customers
  // waiting on 对话, 小爱's line and colour.
  const mayPage = (k: Page) =>
    k === "today"
      ? mayToday
      : k === "reports"
      ? can(me, "view_reports")
      : k === "ai"
        ? can(me, "edit_ai")
        : k === "playbook"
          ? can(me, "edit_playbook")
          : k === "settings"
            ? can(me, "edit_settings")
            : k === "users"
              ? can(me, "manage_users")
              : can(me, "view_leads");
  // 今天's number is 今天要做 in grey, the sum 今天 and 客户表 show (customers.ts todoCounts): 对话's red is only
  // who waits. Not before the orders have come too, so it never moves when 今天 is opened.
  const todayN = can(me, "view_leads") && counts && shop.read ? todoCounts(counts, shop.orders ?? []).all : 0;
  const item = (k: Page): SideItem & NavItem => ({
    key: k,
    label: PAGE[k].label,
    icon: PAGE[k].icon,
    ...(k === "today" && todayN > 0 ? { badge: todayN, tip: `今天要做 ${todayN} 件` } : {}),
    ...(k === "leads" && waiting > 0 ? { badge: waiting, hot: true, tip: `${waiting} 个客户在等回复` } : {}),
    ...(k === "followup" && sendable > 0 ? { badge: sendable, tip: `今天可以直接发 ${sendable} 位` } : {}),
    ...(k === "ai" && health ? { sub: healthShort(health), tone: aiDot } : {}),
  });
  const sideGroups = SIDE_GROUPS.map((g) => g.filter(mayPage).map(item)).filter((g) => g.length > 0);
  // The phone's bar: the first four of PHONE_ORDER; the rest under 更多, in the sidebar's order.
  const barKeys = PHONE_ORDER.filter(mayPage).slice(0, 4);
  const barItems = barKeys.map(item);
  const moreItems = SIDE_GROUPS.flat().filter((k) => mayPage(k) && !barKeys.includes(k)).map(item);
  const label = safeView === "live" ? "" : PAGE[safeView].label;


  return (
    <div className={`app${fold.folded ? " collapsed" : ""}${fold.snap ? " sn-snap" : ""}${safeView === "leads" && chatOpen ? " chat-open" : ""}`}>
      <LoadBar />
      {/* The frame (prototype v2.20): the sidebar on a computer; on a phone a
          bar on top (the logo, where you are, who you are) and PhoneNav along
          the bottom. Not under the TV board, which is the whole screen. */}
      {safeView !== "live" && (
        <SideNav
          me={me}
          groups={sideGroups}
          view={menuView}
          reading={!!pending}
          fold={fold}
          fresh={fresh}
          onPick={pick}
          onNews={openNews}
          onSignOut={() => void signOut()}
        />
      )}
      {safeView !== "live" && (
        <div className="ptop">
          <span className="sn-mark" aria-hidden="true">
            LZ
          </span>
          <b className="ptop-t">{label}</b>
          <AccountMenu me={me} onSignOut={() => void signOut()} compact />
        </div>
      )}

      {/* All three views stay mounted once opened: switching tabs keeps the
          open conversation, the report date range and a half-filled new-user
          form. `active` pauses the leads poll while it is hidden. */}
      {can(me, "view_leads") && (
        <Viewport on={safeView === "leads"} warm={warmLeads}>
          <LeadsView
            me={me}
            active={safeView === "leads"}
            onCount={leadsCount}
            onWaiting={setWaiting}
            onCounts={onCounts}
            openRequest={openReq}
            onBackTo={chatClosed}
            onChatClosed={chatClosed}
            onChatOpen={setChatOpen}
            onChatDrawn={chatDrawn}
          />
        </Viewport>
      )}
      {nothing && (
        <div className="viewport">
          <p className="empty">这个角色还没有能打开的页面</p>
        </div>
      )}
      {drawn.includes("today") && mayToday && (
        <Viewport on={safeView === "today"}>
          <ViewShell page="today">
            <TodayView
              me={me}
              active={safeView === "today"}
              health={health}
              counts={counts}
              onOpenChat={openFromToday}
              onGo={pick}
            />
          </ViewShell>
        </Viewport>
      )}
      {drawn.includes("customers") && can(me, "view_leads") && (
        <Viewport on={safeView === "customers"}>
          <ViewShell page="customers">
            <CustomersView me={me} active={safeView === "customers"} onOpen={openFromCustomers} />
          </ViewShell>
        </Viewport>
      )}
      {drawn.includes("followup") && can(me, "view_leads") && (
        <Viewport on={safeView === "followup"}>
          <ViewShell page="followup">
            <FollowupView me={me} active={safeView === "followup"} onOpen={openFromFollowup} />
          </ViewShell>
        </Viewport>
      )}
      {drawn.includes("prices") && can(me, "view_leads") && (
        <Viewport on={safeView === "prices"}>
          <PricesView active={safeView === "prices"} mayEditSG={can(me, "edit_settings")} />
        </Viewport>
      )}
      {drawn.includes("reports") && can(me, "view_reports") && (
        <Viewport on={safeView === "reports"}>
          <ViewShell page="reports">
            <GrowthView me={me} active={safeView === "reports"} onOpen={openFromReports} onOpenCustomers={openCustomers} />
          </ViewShell>
        </Viewport>
      )}
      {drawn.includes("board") && can(me, "view_leads") && (
        <Viewport on={safeView === "board"}>
          <ViewShell page="board">
            <ScriptBoardView me={me} active={safeView === "board"} onOpenAI={openAI} />
          </ViewShell>
        </Viewport>
      )}
      {drawn.includes("playbook") && can(me, "edit_playbook") && (
        <Viewport on={safeView === "playbook"}>
          <ViewShell page="playbook">
            <PlaybookView active={safeView === "playbook"} />
          </ViewShell>
        </Viewport>
      )}
      {drawn.includes("ai") && can(me, "edit_ai") && (
        <Viewport on={safeView === "ai"}>
          <ViewShell page="ai">
            <AIView
              active={safeView === "ai"}
              me={me}
              onOpenChat={openFromAI}
              health={health}
              library={(shown) => (
                <ViewShell wait={<PageSkel page="ai" word="素材库" kind="table" bare />}>
                  <LibraryView active={shown} />
                </ViewShell>
              )}
              details={
                can(me, "edit_settings") ? (
                  <ViewShell wait={<PageSkel page="ai" word="细节设定" kind="rows" bare />}>
                    <SettingsView me={me} shown={safeView === "ai"} part="ai" />
                  </ViewShell>
                ) : undefined
              }
            />
          </ViewShell>
        </Viewport>
      )}
      {drawn.includes("settings") && can(me, "edit_settings") && (
        <Viewport on={safeView === "settings"}>
          <ViewShell page="settings">
            <SettingsView me={me} shown={safeView === "settings"} onOpenAI={openAI} />
          </ViewShell>
        </Viewport>
      )}
      {drawn.includes("users") && can(me, "manage_users") && (
        <Viewport on={safeView === "users"}>
          <ViewShell page="users">
            <UsersView me={me} active={safeView === "users"} onMeChanged={reloadMe} health={health} />
          </ViewShell>
        </Viewport>
      )}
      {/* Mounted only while it is up: a screen nobody is looking at asks for nothing. */}
      {safeView === "live" && (
        <ViewShell>
          <LiveBoard onExit={exitLive} />
        </ViewShell>
      )}
      {/* Drawn only on a phone (shell.css). */}
      {safeView !== "live" && (
        <PhoneNav
          me={me}
          view={menuView}
          items={barItems}
          more={moreItems}
          pending={pending}
          fresh={fresh}
          onPick={pick}
          onNews={openNews}
          onSignOut={() => void signOut()}
        />
      )}
      {newsOpen && <NewsDrawer onClose={shutNews} onGo={pick} />}
    </div>
  );
}
